import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from './firebase'; 
import StudentCard from './StudentCard';
import AddStudentForm from './AddStudentForm';
import { calculateDeadline } from './complianceMath'; 

const ComplianceDashboard = ({ tenantId }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [schools, setSchools] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('students'); 
  const [showAddForm, setShowAddForm] = useState(false);

  // Filters and Sorting
  const [searchTerm, setSearchTerm] = useState('');
  const [filterManager, setFilterManager] = useState('All');
  const [sortBy, setSortBy] = useState('alphabetical'); 

  // Engine State
  const [calendar, setCalendar] = useState(null);
  const [testReferralDate, setTestReferralDate] = useState('');
  const [calculatedDeadline, setCalculatedDeadline] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (!auth.currentUser) return;

        // 1. Authenticate and get User Profile
        const userRef = doc(db, 'districts', tenantId, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (!userSnap.exists()) {
          setError("User profile not found in database.");
          setLoading(false);
          return;
        }
        
        const profile = userSnap.data();
        setCurrentUser(profile);

        // 2. Fetch Students (Enforcing FERPA Role-Based Access)
        const studentsRef = collection(db, 'districts', tenantId, 'students');
        let studentQuery;

        if (profile.role === 'Case Manager') {
          // ITINERANT RULE: Case managers see ALL their students, regardless of building.
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", auth.currentUser.uid));
        } else if (profile.role === 'Instructional Assistant') {
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", profile.assignedCaseManager));
        } else if (profile.role === 'ECE Recorder') {
          // MULTI-BUILDING RULE: ECE Recorders see students assigned to them as auditor.
          studentQuery = query(studentsRef, where("assignedEceRecorder", "==", auth.currentUser.uid));
        } else {
          studentQuery = query(studentsRef); // Admins see everything
        }
        
        const studentDocs = await getDocs(studentQuery);
        
        // DEPRECATED HARDCODED MATH REMOVED: StudentCard handles all math dynamically now!
        setStudents(studentDocs.docs.map(doc => ({
          id: doc.id, 
          ...doc.data()
        })));

        // 3. Fetch Staff Directory
        const staffRef = collection(db, 'districts', tenantId, 'users');
        const staffDocs = await getDocs(staffRef);
        let allStaff = staffDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        if (profile.role !== 'SuperAdmin') {
          allStaff = allStaff.filter(member => member.role !== 'SuperAdmin');
        }
        setStaff(allStaff);

        // 4. Fetch the Continuous Master Timeline
        try {
          const districtRef = doc(db, 'districts', tenantId);
          const districtSnap = await getDoc(districtRef);
          
          let masterHolidays = [];
          if (districtSnap.exists()) {
            masterHolidays = districtSnap.data().nonInstructionalDays || [];
          }

          const settingsRef = collection(db, 'districts', tenantId, 'settings');
          const settingsSnap = await getDocs(settingsRef);
          
          const boundaries = {};
          settingsSnap.forEach(docSnap => {
            if (docSnap.id.startsWith('calendar-')) {
              const calData = docSnap.data();
              if (calData.academicYear && calData.startDate && calData.endDate) {
                boundaries[calData.academicYear] = {
                  startDate: calData.startDate,
                  endDate: calData.endDate
                };
              }
            }
          });

          setCalendar({
            academicYear: 'Continuous Timeline',
            holidaysAndBreaks: masterHolidays,
            yearBoundaries: boundaries
          });
        } catch (calErr) {
          console.error("Error fetching master calendar:", calErr);
        }

        // 5. Fetch the Dynamic Schools List
        try {
          const schoolsRef = doc(db, 'districts', tenantId, 'settings', 'schools');
          const schoolsSnap = await getDoc(schoolsRef);
          if (schoolsSnap.exists() && schoolsSnap.data().schoolList) {
            setSchools(schoolsSnap.data().schoolList);
          }
        } catch (schoolErr) {
          console.error("Error fetching schools:", schoolErr);
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load dashboard.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [tenantId, showAddForm]); 

  // --- Test Calculator ---
  const handleTestCalculation = () => {
    if (!calendar || !calendar.holidaysAndBreaks) {
      alert("Calendar data is missing. Please contact a SuperAdmin.");
      return;
    }
    if (!testReferralDate) {
      alert("Please enter a referral date.");
      return;
    }

    const deadline = calculateDeadline(testReferralDate, 60, calendar.holidaysAndBreaks, calendar.yearBoundaries);
    const [year, month, day] = deadline.split('-');
    setCalculatedDeadline(`${month}/${day}/${year}`);
  };

  const getStaffName = (uid) => {
    const found = staff.find(s => s.id === uid);
    return found ? found.name : 'Unassigned';
  };

  // --- The Transfer Engine (Now handles ECE Recorders too) ---
  const handleTransferStudent = async (studentId, newSchoolId, newCaseManagerId, newEceRecorderId) => {
    try {
      const studentRef = doc(db, 'districts', tenantId, 'students', studentId);
      
      const updateData = {
        schoolId: newSchoolId,
        assignedCaseManager: newCaseManagerId,
        assignedEceRecorder: newEceRecorderId,
        lastTransferDate: new Date().toISOString()
      };

      await updateDoc(studentRef, updateData);
      
      setStudents(prev => prev.map(s => 
        s.id === studentId ? { ...s, ...updateData } : s
      ));
      
      alert("Student successfully transferred!");
    } catch (error) {
      console.error("Transfer failed:", error);
      alert("Error: You do not have permission to transfer this student.");
    }
  };

  const handleDeleteStudent = async (studentId, studentName) => {
    const isConfirmed = window.confirm(`Are you sure you want to completely delete ${studentName}'s record?`);
    if (!isConfirmed) return;
    try {
      await deleteDoc(doc(db, 'districts', tenantId, 'students', studentId));
      setStudents(prev => prev.filter(s => s.id !== studentId));
    } catch (error) {
      alert("Error deleting student.");
    }
  };

  // --- Permissions Check ---
  const isAdmin = currentUser?.role === 'SuperAdmin' || currentUser?.role === 'District Admin';
  const isClassroomStaff = currentUser?.role === 'Instructional Assistant';
  const canFilter = isAdmin || currentUser?.role === 'ECE Recorder'; 

  // --- Sorting & Filtering Logic ---
  let processedStudents = [...students];

  if (searchTerm) {
    processedStudents = processedStudents.filter(s => {
      const name = s.name || s.studentName || '';
      return name.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }

  if (filterManager !== 'All') {
    processedStudents = processedStudents.filter(s => s.assignedCaseManager === filterManager);
  }

  if (sortBy === 'alphabetical') {
    processedStudents.sort((a, b) => {
      const nameA = (a.name || a.studentName || '').toLowerCase();
      const nameB = (b.name || b.studentName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  } else if (sortBy === 'urgency') {
    processedStudents.sort((a, b) => {
      const getClosestDate = (student) => {
        // NEW LOGIC: If consent is obtained, deprioritize this student in the urgency view.
        if (student.consentObtainedDate) return new Date('2999-12-31'); 

        const dates = [];
        if (student.initialTracking && student.initialDueDate) dates.push(new Date(student.initialDueDate));
        if (!student.initialTracking) {
          if (student.annualIepDate) dates.push(new Date(student.annualIepDate));
          if (student.reevalDate) dates.push(new Date(student.reevalDate));
        }
        if (dates.length === 0) return new Date('2999-12-31'); 
        return new Date(Math.min(...dates));
      };
      return getClosestDate(a) - getClosestDate(b);
    });
  }

  if (loading) return <div style={{ padding: '20px' }}>Loading vault...</div>;
  if (error) return <div style={{ padding: '20px', color: 'red' }}>{error}</div>;

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      {isAdmin && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button 
            onClick={() => setActiveTab('students')}
            style={{ padding: '10px 20px', backgroundColor: activeTab === 'students' ? '#003366' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
            Compliance Dashboard
          </button>
          <button 
            onClick={() => setActiveTab('staff')}
            style={{ padding: '10px 20px', backgroundColor: activeTab === 'staff' ? '#ba0c2f' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
            Staff Directory
          </button>
        </div>
      )}

      {activeTab === 'students' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #003366', paddingBottom: '10px', marginBottom: '20px' }}>
            <div>
              <h2 style={{ margin: 0, color: '#003366' }}>Compliance Caseload</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '5px' }}>
                <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Access Level: {currentUser?.role}</p>
                
                {!isClassroomStaff && (
                  calendar ? (
                    <span style={{ backgroundColor: '#e6f0fa', color: '#003366', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ height: '6px', width: '6px', backgroundColor: '#28a745', borderRadius: '50%', display: 'inline-block' }}></span>
                      Engine Synced: {calendar.academicYear}
                    </span>
                  ) : (
                    <span style={{ backgroundColor: '#ffe6e6', color: '#ba0c2f', padding: '3px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ height: '6px', width: '6px', backgroundColor: '#ba0c2f', borderRadius: '50%', display: 'inline-block' }}></span>
                      Calendar Not Synced
                    </span>
                  )
                )}
              </div>
            </div>
            <span style={{ backgroundColor: '#e6f0fa', padding: '5px 10px', borderRadius: '15px', fontSize: '14px', fontWeight: 'bold', color: '#003366' }}>
              {processedStudents.length} Students
            </span>
          </div>

          {canFilter && (
            <div style={{ backgroundColor: '#f4f4f4', padding: '15px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Search Students</label>
                <input 
                  type="text" 
                  placeholder="Type a name..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginTop: '4px' }}
                />
              </div>

              <div style={{ flex: '1 1 200px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Filter by Case Manager</label>
                <select 
                  value={filterManager}
                  onChange={(e) => setFilterManager(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginTop: '4px' }}
                >
                  <option value="All">All Staff</option>
                  {staff
                    .filter(s => s.role === 'Case Manager')
                    .map(cm => (
                      <option key={cm.id} value={cm.id}>{cm.name}</option>
                  ))}
                </select>
              </div>

              <div style={{ flex: '1 1 200px' }}>
                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666' }}>Sort View</label>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', marginTop: '4px' }}
                >
                  <option value="alphabetical">Alphabetical (A-Z)</option>
                  <option value="urgency">Urgency (Closest Deadline First)</option>
                </select>
              </div>
            </div>
          )}

          {!isClassroomStaff && currentUser?.role !== 'ECE Recorder' && !showAddForm && (
            <button 
              onClick={() => setShowAddForm(true)}
              style={{ marginBottom: '20px', padding: '10px 20px', backgroundColor: '#003366', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              + Add New Student
            </button>
          )}

          {showAddForm && (
            <AddStudentForm 
              tenantId={tenantId} 
              schools={schools} 
              onSuccess={() => setShowAddForm(false)} 
              onCancel={() => setShowAddForm(false)} 
            />
          )}

          {processedStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>No students found matching these filters.</p>
            </div>
          ) : (
            processedStudents.map(student => (
              <StudentCard 
                key={student.id} 
                student={student} 
                tenantId={tenantId} 
                caseManagerName={getStaffName(student.assignedCaseManager)}
                eceRecorderName={getStaffName(student.assignedEceRecorder)} 
                currentUserRole={currentUser?.role}
                onDelete={handleDeleteStudent}
                calendar={calendar} 
                schools={schools}
                staffList={staff.filter(s => s.role === 'Case Manager')}
                eceRecordersList={staff.filter(s => s.role === 'ECE Recorder')} 
                onTransfer={handleTransferStudent}
              />
            ))
          )}
        </>
      )}

      {activeTab === 'staff' && isAdmin && (
        <>
          <div style={{ borderBottom: '2px solid #ba0c2f', paddingBottom: '10px', marginBottom: '20px' }}>
            <h2 style={{ margin: '0', color: '#ba0c2f' }}>Staff Directory</h2>
            <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>View all active staff members. Manage permissions in District Settings.</p>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <thead>
              <tr style={{ backgroundColor: '#f4f4f4', textAlign: 'left' }}>
                <th style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>Name / Email</th>
                <th style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>Building Assignment</th>
                <th style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>Role Assignment</th>
                <th style={{ padding: '12px', borderBottom: '1px solid #ddd' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(member => (
                <tr key={member.id}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                    <strong>{member.name || 'Pending Staff'}</strong><br/>
                    <span style={{ fontSize: '12px', color: '#666' }}>{member.email}</span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee', color: '#555' }}>
                    {member.schoolId || 'Unassigned'}
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                    <span style={{ padding: '4px 8px', backgroundColor: '#e6f0fa', color: '#003366', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                      {member.role || 'No Role'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                    <span style={{ 
                      padding: '4px 8px', 
                      backgroundColor: member.status === 'Active' ? '#e6ffe6' : '#fff3e6', 
                      color: member.status === 'Active' ? '#006600' : '#cc7a00', 
                      borderRadius: '4px', 
                      fontSize: '12px', 
                      fontWeight: 'bold' 
                    }}>
                      {member.status || 'Active'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default ComplianceDashboard;